import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { COLORS } from '../commons/Colors'
import CustomModalComponent from '../commons/components/CustomModalComponent'
import RoundButtonComponent from '../commons/components/RoundButtonComponent'
import {
    FeatureFlag,
    FeatureFlagItem,
    getAllUsers,
    UserOption,
} from './API/FeatureFlagsAPI'
import PlatformBlock, { PlatformConfig } from './components/PlatformBlock'
import { getFlagInfo } from './flagCatalog'

type Props = {
    isOpen: boolean
    flag: FeatureFlagItem | null
    onClose: () => void
    onSave: (data: FeatureFlag) => Promise<void>
}

type FormState = {
    app: PlatformConfig
    web: PlatformConfig
}

const EMPTY_FORM: FormState = {
    app: { enabled: true, disabledForUsers: [], disabledForRoles: [] },
    web: { enabled: true, disabledForUsers: [], disabledForRoles: [] },
}

const hydratePlatform = (
    flag: FeatureFlagItem,
    platform: 'app' | 'web'
): PlatformConfig => ({
    enabled: flag[platform]?.enabled ?? flag.enabled ?? true,
    disabledForUsers:
        flag[platform]?.disabledForUsers ?? flag.disabledForUsers ?? [],
    disabledForRoles:
        flag[platform]?.disabledForRoles ?? flag.disabledForRoles ?? [],
})

export default function FeatureFlagFormModal({
    isOpen,
    flag,
    onClose,
    onSave,
}: Props) {
    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [isSaving, setIsSaving] = useState(false)
    const [allUsers, setAllUsers] = useState<UserOption[]>([])

    useEffect(() => {
        if (isOpen) {
            getAllUsers().then(setAllUsers)
        }
    }, [isOpen])

    useEffect(() => {
        if (flag && isOpen) {
            setForm({
                app: hydratePlatform(flag, 'app'),
                web: hydratePlatform(flag, 'web'),
            })
        }
    }, [flag, isOpen])

    const handleSubmit = async () => {
        setIsSaving(true)
        try {
            await onSave({
                enabled: form.app.enabled || form.web.enabled,
                enabledForUsers: [],
                disabledForUsers: [],
                disabledForRoles: [],
                app: {
                    enabled: form.app.enabled,
                    enabledForUsers: [],
                    disabledForUsers: form.app.disabledForUsers,
                    disabledForRoles: form.app.disabledForRoles,
                },
                web: {
                    enabled: form.web.enabled,
                    enabledForUsers: [],
                    disabledForUsers: form.web.disabledForUsers,
                    disabledForRoles: form.web.disabledForRoles,
                },
            })
        } finally {
            setIsSaving(false)
        }
    }

    if (!flag) return null

    const info = getFlagInfo(flag.documentId)

    return (
        <CustomModalComponent
            isVisible={isOpen}
            onClose={onClose}
            title={`Configurar: ${flag.documentId}`}
        >
            <ModalContent>
                <InfoBox>
                    <strong>Flag:</strong> {flag.documentId}
                    {flag.description && (
                        <>
                            <br />
                            <strong>Descrição:</strong> {flag.description}
                        </>
                    )}
                </InfoBox>

                <PlatformBlock
                    title="APP"
                    allUsers={allUsers}
                    value={form.app}
                    onChange={(app) => setForm((prev) => ({ ...prev, app }))}
                    usage={info?.app}
                />

                <PlatformBlock
                    title="WEB"
                    allUsers={allUsers}
                    value={form.web}
                    onChange={(web) => setForm((prev) => ({ ...prev, web }))}
                    usage={info?.web}
                />

                <ButtonRow>
                    <RoundButtonComponent
                        onClick={onClose}
                        text="Cancelar"
                        color="secondary"
                    />
                    <RoundButtonComponent
                        onClick={handleSubmit}
                        text={isSaving ? 'Salvando...' : 'Salvar'}
                        color="primary"
                        disabled={isSaving}
                    />
                </ButtonRow>
            </ModalContent>
        </CustomModalComponent>
    )
}

const ModalContent = styled.div`
    min-width: 500px;
    font-family: Roboto;
`

const InfoBox = styled.div`
    background: #f5f5f5;
    border: 1px solid ${COLORS.borderGray};
    border-radius: 4px;
    padding: 12px;
    margin-bottom: 20px;
    font-size: 13px;
    color: ${COLORS.primaryGray};
    line-height: 1.5;
`

const ButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid ${COLORS.borderGray};
`
